import { useCallback, useState, useEffect, useRef } from "react";
import useVerifyClaim from "@/hooks/useVerifyClaim";
import { useContractWrite, useSwitchNetwork, useNetwork } from "wagmi";
import { waitForTransaction } from "wagmi/actions";
import { requestMintClaimerSignature } from "@/services/network/claimer";
import useAccount from "@/hooks/useAccount";
import stores, { actions } from "@/stores";
import useClaimerContract from "@/hooks/useClaimerContract";
import useMintReferral from "@/hooks/useMintReferral";
import mixpanel from "mixpanel-browser";
import { isSkipError } from "@/utils/validate";
import { claimerContracts } from "@/services/contract";

function parseFormValidatorId(form: any) {
  const validatorId = !form.validator
    ? "13"
    : form.validator === "empty"
    ? "13"
    : form.validator;
  return validatorId;
}

export default function useMintChainClaimer(chainId: number) {
  const { address, isLogin, login } = useAccount();
  const { chain } = useNetwork();

  const contract = claimerContracts[chainId];

  const [minting, setMinting] = useState(false);
  const { switchNetworkAsync } = useSwitchNetwork();
  const { form } = useVerifyClaim();
  const { lieutenantAddress, isValidLieutenant, hasParamLieutenant } =
    useMintReferral(chainId);
  const [nftMeta, setNftMeta] = useState<{
    score: number;
    role: number;
    validator: number;
  } | null>(null);

  const claimerContractInfo = useClaimerContract(chainId);
  // @ts-ignore
  const { writeAsync: mintClaimerByContract } = useContractWrite({
    ...contract,
    functionName: "mintClaimerwithSig",
  });

  const contractMint = useCallback(async () => {
    setMinting(true);
    try {
      if (!isLogin) {
        login();
        return false;
      }
      if (chain?.id !== chainId) {
        // @ts-ignore
        await switchNetworkAsync(chainId);
      }
      console.log("form", form);
      const validatorId = parseFormValidatorId(form);
      console.log("validatorId", validatorId);
      const result = await requestMintClaimerSignature({
        walletAddress: address as any,
        validatorTokenId: validatorId,
        lieutenantAddr:
          lieutenantAddress || "0x0000000000000000000000000000000000000000",
      });

      setNftMeta({
        ...result.message,
        validator: validatorId,
      });

      const res = await mintClaimerByContract({
        args: [
          address,
          parseInt(validatorId || "0"),
          parseInt(result.message.score as any),
          lieutenantAddress || "0x0000000000000000000000000000000000000000",
          parseInt(result.message.role as any),
          result.signedMessage,
        ],
      });

      try {
        const transReceipt = await waitForTransaction(res);
        if (transReceipt.status === "reverted") {
          throw new Error("Mint fail");
        }
      } catch (e: any) {
        if (!(await isSkipError(e))) {
          throw e;
        }
      }

      mixpanel.track("claimer_minted", {
        walletAddress: address as any,
        validatorTokenId: validatorId,
        lieutenantAddr:
          lieutenantAddress || "0x0000000000000000000000000000000000000000",
        mintType: "contract",
      });
      stores.dispatch(
        actions.mint.setHistory({
          history: null,
          type: "claimer",
        })
      );
      return true;
    } finally {
      setMinting(false);
    }
  }, [
    mintClaimerByContract,
    switchNetworkAsync,
    chain,
    address,
    chainId,
    isLogin,
    login,
    form,
    lieutenantAddress,
  ]);

  return {
    minting,
    claimerContractInfo,
    defaultDepositCost: 0.02,
    nftMeta,
    isValidLieutenant,
    hasParamLieutenant,
    contractMint,
  };
}
